/**
 * Mock-test result types + a pure presenter, matching the backend `MockTestReport`
 * (server/src/pipeline/exam/mockTestGrader.ts) sent over WS as `{type:'mock_test_result', report}`.
 *
 * The backend deliberately produces an HONEST readiness verdict + per-Teil points, NOT a fake
 * percentage score (that was the old mockTestScore heuristic we removed on 2026-06-14). So the UI
 * leads with pass / needs-work + the verdict + a recap of recurring mistakes; the points are shown
 * as a clearly-labelled estimate.
 *
 * The displayed total is derived from the per-Teil points (sum of points / sum of max) so it is
 * always self-consistent with the per-Teil breakdown shown right beside it. We intentionally do NOT
 * surface the backend's `estimatedTotal`, which is normalised to a different module scale and would
 * not match the per-Teil sum on screen.
 */

export interface TeilPoints {
  teil: string;   // 'teil1' | 'teil2' | 'teil3'
  points: number; // earned, scaled to this Teil's max
  max: number;
}

export interface MockTestReport {
  teils: TeilPoints[];
  estimatedTotal: number;  // backend's module-scaled estimate (not shown; see file note)
  passed: boolean;
  isEstimate: true;        // always true — an AI estimate, never an official score
  recap: string[];         // recurring-mistake lines
  verdict: string;         // one-line ready / needs-work readiness note
}

export interface MockTeilView {
  label: string;  // 'Teil 1'
  points: number;
  max: number;
  pct: number;    // 0..100, for the progress bar width
}

export interface MockReportView {
  headline: string;        // 'Passed' | 'Needs work'
  passed: boolean;
  earned: number;          // sum of per-Teil points
  totalMax: number;        // sum of per-Teil maxes
  teils: MockTeilView[];
  recap: string[];
  verdict: string;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Pure transform of a backend report into display-ready values. Tolerates missing fields. */
export function formatMockReport(report: MockTestReport): MockReportView {
  const teils = (report.teils ?? []).map((t) => ({
    label: String(t.teil ?? '').replace('teil', 'Teil '),
    points: t.points ?? 0,
    max: t.max ?? 0,
    pct: t.max > 0 ? Math.round((t.points / t.max) * 100) : 0,
  }));
  const earned = round2(teils.reduce((s, t) => s + t.points, 0));
  const totalMax = round2(teils.reduce((s, t) => s + t.max, 0));
  return {
    headline: report.passed ? 'Passed' : 'Needs work',
    passed: !!report.passed,
    earned,
    totalMax,
    teils,
    recap: (report.recap ?? []).filter((line) => typeof line === 'string' && line.trim().length > 0),
    verdict: (report.verdict ?? '').trim(),
  };
}
